import { zodResolver } from "@hookform/resolvers/zod"
import { Calendar as CalendarIcon } from "@untitledui/icons"
import { useEffect } from "react"
import { Controller, useForm } from "react-hook-form"
import { useShallow } from "zustand/react/shallow"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { SectionFooter } from "@/components/ui/section-footer"
import { SectionHeader } from "@/components/ui/section-header"
import { SectionLabel } from "@/components/ui/section-label"

import { useFormTabsStore } from "../../components"
import { useCheckinForm } from "../../stores/useCheckinFormStore"
import type { AccountProps, BookingProps, CohortProps } from "../../types"
import { SchoolContact, TimePicker } from "../fields"

import { detailsSchema } from "./details.schema"

const formatDate = (iso: string) =>
  new Intl.DateTimeFormat("en-AU", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(iso + "T00:00"))

interface IDetails {
  booking: BookingProps
  cohort: CohortProps | null
  account: AccountProps
  readOnly?: boolean
}

const Details = ({ booking, cohort, account, readOnly = false }: IDetails) => {
  const defaultData = useCheckinForm(
    useShallow((s) => s.getDefaultValues(detailsSchema.keyof().options)),
  )

  const updateForm = useCheckinForm((s) => s.updateForm)

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitted },
    subscribe,
  } = useForm({
    resolver: readOnly ? undefined : zodResolver(detailsSchema),
    mode: "onSubmit",
    reValidateMode: "onBlur",
    defaultValues: {
      program_start_time:
        booking.program_start_time || defaultData.program_start_time || "",
      expected_cohort_size:
        cohort?.expected_size ?? defaultData?.expected_cohort_size ?? 0,
      booking_manager: defaultData.booking_manager ?? "",
    },
    disabled: readOnly,
  })
  const onNext = useFormTabsStore((s) => s.onNext)
  const setTabState = useFormTabsStore((s) => s.setStatus)

  useEffect(() => {
    // make sure to unsubscribe;
    const callback = subscribe({
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        updateForm(values)
      },
    })

    return () => callback()
  }, [subscribe, updateForm])

  const handleNext = handleSubmit(
    (data) => {
      setTabState("complete")
      updateForm(data)
      onNext()
    },
    () => {
      setTabState("error")
    },
  )

  return (
    <form onSubmit={handleNext} noValidate>
      <div className="flex flex-col gap-6">
        <SectionHeader.Root>
          <SectionHeader.Group>
            <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
              <SectionHeader.Heading>Booking Details</SectionHeader.Heading>
              <SectionHeader.Subheading>
                Confirm the session logistics
              </SectionHeader.Subheading>
            </div>
          </SectionHeader.Group>
        </SectionHeader.Root>

        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
            <SectionLabel.Root
              size="sm"
              title="School"
              className="max-lg:hidden"
            />
            <div>
              <Input
                label="School/Organisation Name"
                value={account.name}
                isDisabled
              />
            </div>
          </div>

          <hr className="bg-border-secondary h-px w-full border-none" />

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
            <SectionLabel.Root
              size="sm"
              title="Schedule"
              className="max-lg:hidden"
            />
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:gap-6">
              <div>
                <Input
                  label="Date of Program"
                  value={
                    booking.program_date ? formatDate(booking.program_date) : ""
                  }
                  icon={CalendarIcon}
                  isDisabled
                />
              </div>

              <Controller
                control={control}
                name="program_start_time"
                render={({
                  field: { onChange, value, disabled },
                  fieldState: { error },
                }) => (
                  <div className="flex items-start gap-2">
                    <TimePicker
                      label="Time of Program Start"
                      value={value}
                      onChange={onChange}
                      window={
                        booking.program_start_time
                          ? { time: booking.program_start_time, step: 60 }
                          : undefined
                      }
                      step={5}
                      error={error}
                      isDisabled={disabled}
                      isRequired
                    />
                  </div>
                )}
              />
            </div>
          </div>

          <hr className="bg-border-secondary h-px w-full border-none" />

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
            <SectionLabel.Root
              size="sm"
              title="Cohort"
              className="max-lg:hidden"
            />
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:gap-6">
              <div>
                <Input
                  label="Year level attending"
                  value={cohort?.year_level ?? ""}
                  isDisabled
                />
              </div>
              <Controller
                control={control}
                name="expected_cohort_size"
                render={({
                  field: { onChange, onBlur, value, disabled },
                  fieldState: { error },
                }) => (
                  <div className="flex items-center gap-2">
                    <span className="text-quaternary shrink-0 text-sm">
                      approx.
                    </span>
                    <Input
                      aria-label="Expected attendance"
                      type="number"
                      value={value === 0 ? "" : String(value)}
                      onChange={(v) => onChange(v === "" ? 0 : Number(v))}
                      onBlur={onBlur}
                      error={error}
                      isDisabled={disabled}
                      isRequired
                    />
                    <span className="text-quaternary shrink-0 text-sm">
                      attendees
                    </span>
                  </div>
                )}
              />
            </div>
          </div>
          <hr className="bg-border-secondary h-px w-full border-none" />
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
            <SectionLabel.Root
              size="sm"
              title="Booking Contact"
              description="Primary contact for this session"
              className="max-lg:hidden"
            />
            <Controller
              control={control}
              name="booking_manager"
              render={({
                field: { onChange, value, onBlur, disabled },
                fieldState: { error },
              }) => (
                <SchoolContact
                  aria-label="Booking Manager"
                  onChange={onChange}
                  value={value}
                  accountId={account.id}
                  onBlur={onBlur}
                  error={error}
                  isDisabled={disabled}
                  isRequired
                />
              )}
            />
          </div>
        </div>
        <SectionFooter.Root>
          <SectionFooter.Actions>
            <Button
              isDisabled={isSubmitted && !isValid}
              color="primary"
              type="submit"
            >
              Next
            </Button>
          </SectionFooter.Actions>
        </SectionFooter.Root>
      </div>
    </form>
  )
}

export default Details
